import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Tooltip,
  Skeleton,
} from "@mui/material";
import {
  Add,
  Search,
  Refresh,
  OpenInNew,
  GitHub,
  Cloud,
  Dns,
  Circle,
  DeleteOutline,
} from "@mui/icons-material";
import {
  fetchApplications,
  deleteApplication,
  setFilters,
} from "../../redux/slices/applicationSlice";
import CreateApplicationDialog from "./CreateApplicationDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import {
  useVerificationGuard,
  VERIFY_MSG,
} from "../../hooks/useVerificationGuard";

const statusStyles = {
  PENDING: { color: "#f59e0b", bg: "#fef3c7", label: "Pending" },
  ACTIVE: { color: "#10b981", bg: "#d1fae5", label: "Active" },
  UPDATING: { color: "#3b82f6", bg: "#dbeafe", label: "Updating" },
  SUSPENDED: { color: "#ef4444", bg: "#fee2e2", label: "Suspended" },
  MAINTENANCE: { color: "#6366f1", bg: "#e0e7ff", label: "Maintenance" },
  DELETED: { color: "#6b7280", bg: "#f3f4f6", label: "Deleted" },
};

const typeIcons = {
  WORDPRESS: <Dns sx={{ fontSize: 18 }} />,
  DOCKER_IMAGE: <Cloud sx={{ fontSize: 18 }} />,
  DOCKER_COMPOSE: <Cloud sx={{ fontSize: 18 }} />,
  GIT_DOCKERFILE: <GitHub sx={{ fontSize: 18 }} />,
};

const typeLabels = {
  WORDPRESS: "WordPress",
  DOCKER_IMAGE: "Docker Image",
  DOCKER_COMPOSE: "Compose",
  GIT_DOCKERFILE: "Git",
};

const Applications = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isVerifiedOrAdmin = useVerificationGuard();
  const { applications, loading, error, filters } = useSelector(
    (state) => state.applications,
  );
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(filters.search || "");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchApplications(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (searchValue !== filters.search) {
        dispatch(setFilters({ search: searchValue }));
      }
    }, 400);
    return () => clearTimeout(t);
  }, [searchValue, dispatch, filters.search]);

  const handleCreate = () => {
    if (!isVerifiedOrAdmin) {
      setVerifyDialogOpen(true);
      return;
    }
    setCreateDialogOpen(true);
  };

  const handleDeleteClick = (e, app) => {
    e.stopPropagation();
    if (!isVerifiedOrAdmin) {
      setVerifyDialogOpen(true);
      return;
    }
    setDeleteTarget(app);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await dispatch(deleteApplication(deleteTarget.id));
    setDeleteTarget(null);
  };

  const protocol =
    process.env.REACT_APP_SSL_ENABLED === "true" ? "https" : "http";

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: "auto" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Applications
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Deploy and manage your Docker applications
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreate}
          disableElevation
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          New Application
        </Button>
      </Box>

      {/* Search */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 3, alignItems: "center" }}>
        <TextField
          size="small"
          placeholder="Search applications..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 20, color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            maxWidth: 400,
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
          }}
        />
        <Tooltip title="Refresh">
          <IconButton
            onClick={() => dispatch(fetchApplications(filters))}
            disabled={loading}
            size="small"
          >
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && applications.length === 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={72}
              sx={{ borderRadius: 2 }}
            />
          ))}
        </Box>
      )}

      {/* Empty */}
      {!loading && applications.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 10,
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 3,
          }}
        >
          <Cloud sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" fontWeight={600} gutterBottom>
            No applications yet
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 400, mx: "auto" }}
          >
            Deploy any Docker container from a Git repository or Docker Hub
            image
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
            disableElevation
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            Create your first app
          </Button>
        </Box>
      )}

      {/* App List */}
      {applications.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {applications.map((app) => {
            const status = statusStyles[app.status] || statusStyles.PENDING;
            const services = app.services || [];
            const running = services.filter(
              (s) => s.status === "RUNNING",
            ).length;
            const domain = app.customDomain || app.generatedDomain;

            return (
              <Box
                key={app.id}
                onClick={() => navigate(`/dashboard/applications/${app.id}`)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "action.hover",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    bgcolor: "action.selected",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {typeIcons[app.deploymentType] || (
                    <Cloud sx={{ fontSize: 18 }} />
                  )}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.25,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                      {app.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={typeLabels[app.deploymentType]}
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        fontWeight: 500,
                        bgcolor: "action.selected",
                      }}
                    />
                  </Box>
                  {domain && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                    >
                      {domain}
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    alignItems: "center",
                    color: "text.secondary",
                    minWidth: 90,
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {running}/{services.length} running
                  </Typography>
                </Box>

                <Chip
                  size="small"
                  icon={
                    <Circle
                      sx={{
                        fontSize: "8px !important",
                        color: `${status.color} !important`,
                      }}
                    />
                  }
                  label={status.label}
                  sx={{
                    bgcolor: status.bg,
                    color: status.color,
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    height: 26,
                    minWidth: 80,
                    "& .MuiChip-icon": { ml: 0.5 },
                  }}
                />

                {domain && (
                  <Tooltip title="Open in browser">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`${protocol}://${domain}`, "_blank");
                      }}
                      sx={{ color: "text.secondary" }}
                    >
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteClick(e, app)}
                    sx={{
                      color: "text.disabled",
                      "&:hover": { color: "error.main" },
                    }}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      )}

      <CreateApplicationDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Application"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? All services will be stopped and the app will be removed.`}
        confirmText="Delete"
        confirmColor="error"
      />

      <ConfirmDialog
        open={verifyDialogOpen}
        onClose={() => setVerifyDialogOpen(false)}
        onConfirm={() => setVerifyDialogOpen(false)}
        title="Verification Required"
        message={VERIFY_MSG}
        confirmText="OK"
        cancelText=""
      />
    </Box>
  );
};

export default Applications;
